/**
 * @typedef {Object} ContactConfig
 * @property {boolean} [enabled]
 * @property {string} [officialContactUrl]
 * @property {string} [qrAssetPath]
 */

/**
 * @typedef {{ active: true, url: string, qrAssetPath: string }} ActiveContact
 * @typedef {{ active: false, reason: string }} InactiveContact
 * @typedef {ActiveContact | InactiveContact} ContactResolution
 */

export const OFFICIAL_CONTACT_URL = 'https://work.weixin.qq.com/ca/cawcdedfea073e1283';

const ALLOWED_PROTOCOL = 'https:';
const ALLOWED_HOST = 'work.weixin.qq.com';
const ALLOWED_PATHNAME = '/ca/cawcdedfea073e1283';
const ALLOWED_QR_ASSET_PATH = '/contact/add-friend.png';

/**
 * @param {string} reason
 * @returns {InactiveContact}
 */
function inactive(reason) {
  return { active: false, reason };
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * @param {unknown} value
 * @returns {value is string}
 */
function isSafeQrAssetPath(value) {
  return value === ALLOWED_QR_ASSET_PATH;
}

/**
 * @param {string} raw
 * @returns {string | null}
 */
function rejectionReason(raw) {
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return 'url-invalid';
  }
  if (parsed.protocol !== ALLOWED_PROTOCOL) return 'protocol-not-allowed';
  if (parsed.username || parsed.password) return 'userinfo-not-allowed';
  if (parsed.hash) return 'fragment-not-allowed';
  if (parsed.host !== ALLOWED_HOST) return 'host-not-allowed';
  if (parsed.pathname !== ALLOWED_PATHNAME) return 'path-not-allowed';
  if (parsed.search !== '') return 'query-not-allowed';
  if (raw !== OFFICIAL_CONTACT_URL) return 'target-not-allowed';
  return null;
}

/**
 * Resolves the public contact entry from configuration. The accepted target is
 * fixed in source and never derived from the supplied value.
 * @param {ContactConfig | null | undefined} config
 * @returns {ContactResolution}
 */
export function validateContactConfig(config) {
  if (!isPlainObject(config)) return inactive('config-missing');
  if (config.enabled !== true) return inactive('disabled');
  if (typeof config.officialContactUrl !== 'string' || config.officialContactUrl === '') {
    return inactive('url-missing');
  }
  const rejected = rejectionReason(config.officialContactUrl);
  if (rejected) return inactive(rejected);
  if (!isSafeQrAssetPath(config.qrAssetPath)) return inactive('qr-asset-invalid');
  return { active: true, url: OFFICIAL_CONTACT_URL, qrAssetPath: config.qrAssetPath };
}
